import type { DefaultTooltipContentProps } from "recharts";
import type {
  NameType,
  ValueType,
} from "recharts/types/component/DefaultTooltipContent";

import { useWeightEntries } from "@/app/components/legacy-components";
import {
  DeleteWeightEntry,
  EditWeightEntry,
} from "@/app/components/legacy-modals";
import type { WeightEntry } from "@/shared/domains";
import { useBoolean } from "@/shared/hooks";
import { WEIGHT_UNIT } from "@/shared/models";
import { PERIOD } from "@/shared/models/legacy-constants";
import { useSettingsStore } from "@/shared/stores";
import {
  CalendarMinimalisticIcon,
  CloseIcon,
  DocumentMedicineIcon,
  IconWrapper,
  PenIcon,
  TrashBin2Icon,
} from "@/shared/ui";
import { formatBackendDate } from "@/shared/utils";

interface WeightTooltipContentProps {
  tooltipData: DefaultTooltipContentProps<ValueType, NameType>;
  onClose: () => void;
}

export const WeightTooltipContent = ({
  tooltipData,
  onClose,
}: WeightTooltipContentProps) => {
  const { payload } = tooltipData;
  const data = payload?.find((e) => e.name === "x");
  const { view } = useWeightEntries();
  const unitSystem = useSettingsStore((state) => state.unitSystem);

  const {
    value: showDeleteWeightEntryModal,
    setTrue: openDeleteWeightEntryModal,
    setFalse: closeDeleteWeightEntryModal,
  } = useBoolean();

  const {
    value: showEditWeightEntryModal,
    setTrue: openEditWeightEntryModal,
    setFalse: closeEditWeightEntryModal,
  } = useBoolean();

  if (!data?.payload) {
    return null;
  }

  const weightEntry = data.payload as WeightEntry;

  return (
    <div className="pointer-events-auto relative flex w-72 flex-col gap-4 overflow-hidden rounded-2xl border border-neutral-500 bg-neutral-950 p-4 py-6 text-neutral-50">
      <IconWrapper
        onClick={onClose}
        size="sm"
        as="button"
        type="button"
        className="absolute top-1.5 right-1.5"
      >
        <CloseIcon />
      </IconWrapper>

      <header className="flex justify-between text-neutral-400">
        <p className="font-medium">
          {weightEntry.weight} {WEIGHT_UNIT[unitSystem]}
        </p>

        <div className="flex items-center gap-1">
          <IconWrapper size="xs">
            <CalendarMinimalisticIcon />
          </IconWrapper>

          {formatBackendDate(
            weightEntry.date,
            view === PERIOD.YEARLY ? "MM/yyyy" : "MM/dd/yyyy",
          )}
        </div>
      </header>

      <div className="flex flex-col gap-2">
        {weightEntry.note && (
          <div className="flex gap-1">
            <IconWrapper size="sm" className="mt-0.5 text-neutral-500">
              <DocumentMedicineIcon />
            </IconWrapper>

            <p className="line-clamp-3 text-sm">{weightEntry.note}</p>
          </div>
        )}

        {view === PERIOD.MONTHLY && (
          <div className="flex items-center gap-2 self-end">
            <IconWrapper
              size="lg"
              as="button"
              type="button"
              onClick={openDeleteWeightEntryModal}
              className="rounded-lg border border-transparent p-1 hover:opacity-80"
            >
              <TrashBin2Icon />
            </IconWrapper>

            <IconWrapper
              size="lg"
              as="button"
              type="button"
              onClick={openEditWeightEntryModal}
              className="rounded-lg border border-neutral-100 p-1.5 hover:opacity-80"
            >
              <PenIcon />
            </IconWrapper>
          </div>
        )}
      </div>

      <EditWeightEntry
        open={showEditWeightEntryModal}
        onOpenChange={closeEditWeightEntryModal}
        weightEntry={weightEntry}
      />

      <DeleteWeightEntry
        open={showDeleteWeightEntryModal}
        onOpenChange={closeDeleteWeightEntryModal}
        weightEntry={weightEntry}
      />
    </div>
  );
};
